import React, { useEffect, useState, useCallback } from 'react';
import { filterResponsive, rightResponsive } from '../../../common/cssclasses';
import { useTranslate } from '../../../components/TranslationText';
import SemenFilter from '../Components/SemenFilter';
import SemenCard from '../Components/SemenCard';
import { filterSemens, getSemenBuyers } from '../Api/semenMarketApi';
import ActionButton from '../../../components/ActionButton';

const TAKE = 10;

const initialFilters = {
    stallionName: null,
    priceFrom: null,
    priceTo: null,
    ownerId: null,
    skip: 0,
    take: TAKE,
};

export default function SemenMarketListPage() {
    const { translate } = useTranslate();

    const [filters, setFilters] = useState(initialFilters);
    const [horses, setHorses] = useState([]);
    const [buyers, setBuyers] = useState([]);
    const [activeTab, setActiveTab] = useState(null);
    const [totalCount, setTotalCount] = useState(0);

    const hasMore = horses.length < totalCount;

    const fetchSemens = useCallback(async (newFilters, append = false) => {
        const { sort, ...apiFilters } = newFilters;

        const res = await filterSemens(apiFilters);
        setTotalCount(res?.totalCount || 0);
        setHorses(prev =>
            append ? [...prev, ...(res?.result ?? [])] : res?.result ?? []
        );
    }, []);

    useEffect(() => {
        const init = async () => {
            const buyersRes = await getSemenBuyers();
            setBuyers(buyersRes || []);

            const firstBuyer = buyersRes?.[0];
            if (!firstBuyer) return;

            const newFilters = {
                ...initialFilters,
                ownerId: firstBuyer.id,
            };

            setActiveTab(firstBuyer.name);
            setFilters(newFilters);
            fetchSemens(newFilters);
        };

        init();
    }, [fetchSemens]);

    const handleSortChange = (e) => {
        const sortValue = e.target.value;
        const newFilters = {
            ...filters,
            sort: sortValue,
            skip: 0,
        };

        setFilters(newFilters);
        fetchSemens(newFilters);
    };

    const handleFilter = (filterArgs) => {
        const newFilters = {
            ...filters,
            ...filterArgs,
            skip: 0,
        };

        setFilters(newFilters);
        fetchSemens(newFilters);
    };

    const handleTabClick = (buyerName) => {
        const buyer = buyers.find(b => b.name === buyerName);

        const newFilters = {
            ...filters,
            ownerId: buyer?.id || null,
            skip: 0,
        };

        setActiveTab(buyerName);
        setFilters(newFilters);
        fetchSemens(newFilters);
    };

    const handleLoadMore = () => {
        const newFilters = {
            ...filters,
            skip: filters.skip + filters.take,
        };

        setFilters(newFilters);
        fetchSemens(newFilters, true);
    };

    const sortedHorses = [...horses];
    if (filters.sort === 'asc') {
        sortedHorses.sort((a, b) => (a.price || 0) - (b.price || 0));
    } else if (filters.sort === 'desc') {
        sortedHorses.sort((a, b) => (b.price || 0) - (a.price || 0));
    } else if (filters.sort === 'date_asc') {
        sortedHorses.sort((a, b) => new Date(a.stallionBirthDate || a.birthDate || 0) - new Date(b.stallionBirthDate || b.birthDate || 0));
    } else if (filters.sort === 'date_desc') {
        sortedHorses.sort((a, b) => new Date(b.stallionBirthDate || b.birthDate || 0) - new Date(a.stallionBirthDate || a.birthDate || 0));
    } else if (filters.sort === 'name_asc') {
        sortedHorses.sort((a, b) => (a.stallionName || a.name || '').localeCompare(b.stallionName || b.name || ''));
    } else if (filters.sort === 'name_desc') {
        sortedHorses.sort((a, b) => (b.stallionName || b.name || '').localeCompare(a.stallionName || a.name || ''));
    }

    return (
        <div className="row rtl">
            <div className="col-12 d-flex justify-content-between align-items-center mb-main">
                <h2 className="fw-bold mb-0">
                    {translate('web.semenMarket', 'Semen Market')}
                </h2>
                <div className="d-flex justify-content-end">
                    <select
                        className="form-select"
                        style={{ width: '200px', cursor: 'pointer' }}
                        value={filters.sort || ''}
                        onChange={handleSortChange}
                    >
                        <option value="">{translate('web.sort', 'Sort')}</option>
                        <option value="asc">{translate('web.priceLowToHigh', 'Price: Low to High')}</option>
                        <option value="desc">{translate('web.priceHighToLow', 'Price: High to Low')}</option>
                        <option value="date_desc">{translate('web.dateNewestToOldest', 'Date: Newest to Oldest')}</option>
                        <option value="date_asc">{translate('web.dateOldestToNewest', 'Date: Oldest to Newest')}</option>
                        <option value="name_asc">{translate('web.nameAtoZ', 'Name: A to Z')}</option>
                        <option value="name_desc">{translate('web.nameZtoA', 'Name: Z to A')}</option>
                    </select>
                </div>
            </div>

            <div className={filterResponsive}>
                <SemenFilter submit={handleFilter} />
            </div>

            <div className={rightResponsive}>
                <div className="row second_section_bar" style={{ justifyContent: 'flex-start' }}>
                    {[...new Set(buyers.map(b => b?.name))].map((name) => (
                        <div
                            key={name}
                            className="col-lg-4 col-md-4 col-sm-6 col-8 my-2 text-center px-0"
                            style={{ minWidth: 250, cursor: 'pointer' }}
                            onClick={() => handleTabClick(name)}
                        >
                            <span
                                className={`${activeTab === name ? 'fw-bold' : ''} capatalize_text`}
                                style={{ fontSize: 14 }}
                            >
                                {name}
                            </span>

                            <div
                                className="service_bar_underline"
                                style={
                                    activeTab === name
                                        ? { border: '3px solid var(--mainGold)', marginTop: 16 }
                                        : {}
                                }
                            />
                        </div>
                    ))}
                </div>

                <div className="row">
                    {sortedHorses.map(item => (
                        <SemenCard key={item.id} horse={item} />
                    ))}
                </div>

                {hasMore && (
                    <div className="col-12 my-5 text-center">
                        <ActionButton
                            label={translate('web.loadMore', 'Load More')}
                            type="loadmore"
                            onClick={handleLoadMore}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}